import { useEffect, useState, useCallback } from "react";
import { useUrlState } from "@/lib/navState";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, RotateCcw, XCircle, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Plate } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const MGMT = ["Owner", "Admin", "GM", "Branch Manager"];
const LEDGER_ST = { APPROVED: "bg-emerald-100 text-emerald-700", PAID: "bg-blue-100 text-blue-700" };

export default function ProductionFloor() {
  const { user } = useAuth();
  const isLeader = user?.role === "admin" || [...MGMT, "Kepala Bengkel"].includes(user?.role_name);
  const [tab, setTab] = useUrlState("tab", "qc", true);
  return (
    <div data-testid="production-floor" className="mx-auto max-w-5xl">
      <h1 className="font-heading text-xl font-bold text-[#111827] sm:text-2xl">Production Floor</h1>
      <p className="mt-0.5 text-xs text-gray-500">Assign job · QC completion · insentif teknisi (data live)</p>
      <Tabs value={tab} onValueChange={setTab} className="mt-3">
        <TabsList className="flex-wrap">
          <TabsTrigger value="qc" data-testid="tab-qc">QC Queue</TabsTrigger>
          {isLeader && <TabsTrigger value="assign" data-testid="tab-assign">Assign Job</TabsTrigger>}
          <TabsTrigger value="kpi" data-testid="tab-kpi">Technician KPI</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Incentive Ledger</TabsTrigger>
        </TabsList>
        <TabsContent value="qc"><QcQueue /></TabsContent>
        {isLeader && <TabsContent value="assign"><AssignJob /></TabsContent>}
        <TabsContent value="kpi"><KpiTab /></TabsContent>
        <TabsContent value="ledger"><Ledger /></TabsContent>
      </Tabs>
    </div>
  );
}

function QcQueue() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tech/jobs", { params: { status: "WAITING_QC" } }); setJobs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const qc = async (id, decision, reason = "") => {
    try { await api.post(`/tech/jobs/${id}/qc`, { decision, reason }); toast.success(`QC ${decision}`); setView(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  if (loading) return <p className="py-10 text-center text-sm text-gray-400">Memuat…</p>;
  return (
    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {jobs.map((j) => (
        <div key={j.id} data-testid={`qc-job-${j.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <Plate value={j.license_plate} />
            <span className="text-[11px] text-gray-400">{j.technician_name || j.technician_user_id}</span>
          </div>
          {j.wo_number && <div className="mt-1.5"><span className="inline-flex items-center rounded bg-blue-500/15 px-2 py-0.5 font-mono text-xs font-bold text-[#60A5FA] ring-1 ring-inset ring-blue-400/30">{j.wo_number}</span></div>}
          <div className="mt-1.5 truncate text-[13px] font-semibold text-gray-800">{j.work_description}</div>
          <div className="truncate text-xs text-gray-500">{j.panel || "-"} · {j.work_type} · {j.claim_no}</div>
          {(j.complete_photos || []).length > 0 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto">
              {j.complete_photos.map((p, i) => (
                <img key={i} src={`${API}/tech/file?path=${encodeURIComponent(p.storage_path)}`} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
              ))}
            </div>
          )}
          {j.note && <div className="mt-1 text-[11px] text-gray-500">Catatan: {j.note}</div>}
          <div className="mt-2 flex gap-1.5">
            <Button data-testid={`qc-pass-${j.id}`} size="sm" className="h-8 flex-1 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => qc(j.id, "PASS")}><CheckCircle2 size={13} className="mr-1" /> PASS</Button>
            <Button data-testid={`qc-rework-${j.id}`} size="sm" variant="outline" className="h-8 flex-1 border-amber-400 text-xs text-amber-700" onClick={() => setView({ j, mode: "REWORK" })}><RotateCcw size={13} className="mr-1" /> REWORK</Button>
            <Button data-testid={`qc-reject-${j.id}`} size="sm" variant="outline" className="h-8 flex-1 border-red-400 text-xs text-red-600" onClick={() => setView({ j, mode: "REJECT" })}><XCircle size={13} className="mr-1" /> REJECT</Button>
          </div>
        </div>
      ))}
      {jobs.length === 0 && <p className="py-8 text-center text-sm text-gray-400 sm:col-span-2">Tidak ada job menunggu QC.</p>}
      {view && <ReasonDialog view={view} onClose={() => setView(null)} onConfirm={(reason) => qc(view.j.id, view.mode, reason)} />}
    </div>
  );
}

function ReasonDialog({ view, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="text-base">Alasan {view.mode}</DialogTitle></DialogHeader>
        <Textarea data-testid="qc-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tulis alasan…" rows={3} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button data-testid="qc-reason-confirm" disabled={!reason.trim()} onClick={() => onConfirm(reason)}>Konfirmasi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignJob() {
  const [claims, setClaims] = useState([]);
  const [techs, setTechs] = useState([]);
  const [claimId, setClaimId] = useState("");
  const [tech, setTech] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([api.get("/claims", { params: { phase: "REPAIR_ORDER" } }), api.get("/master-users")]);
      setClaims(c.data); setTechs(u.data.filter((x) => x.role_name === "Technician" && x.user_id));
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const claim = claims.find((c) => c.id === claimId);

  const assign = async (wi) => {
    if (!tech) { toast.error("Pilih teknisi dulu"); return; }
    setBusy(wi);
    try { await api.post("/tech/jobs", { claim_id: claimId, work_item_id: wi, technician_user_id: tech }); toast.success("Job di-assign"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
    setBusy("");
  };

  return (
    <div className="mt-2 space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Select value={claimId} onValueChange={setClaimId}>
          <SelectTrigger data-testid="assign-claim" className="h-9 text-sm"><SelectValue placeholder="Pilih unit (Repair Order)" /></SelectTrigger>
          <SelectContent>{claims.map((c) => <SelectItem key={c.id} value={c.id}>{c.license_plate} · {c.claim_no}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={tech} onValueChange={setTech}>
          <SelectTrigger data-testid="assign-tech" className="h-9 text-sm"><SelectValue placeholder="Pilih teknisi" /></SelectTrigger>
          <SelectContent>{techs.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name} ({t.user_id})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {claim && (
        <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
          {(claim.jasa_items || []).map((ji) => (
            <div key={ji.id} className="flex items-center justify-between gap-2 border-t border-gray-100 p-2.5 first:border-t-0">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-gray-800">{ji.work_description || "-"}</div>
                <div className="truncate text-[11px] text-gray-500">{ji.panel || "-"} · {ji.work_type}</div>
              </div>
              <Button data-testid={`assign-btn-${ji.id}`} size="sm" className="h-8 shrink-0 bg-[#111827] text-xs" disabled={busy === ji.id} onClick={() => assign(ji.id)}>{busy === ji.id ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} className="mr-1" /> Assign</>}</Button>
            </div>
          ))}
          {(claim.jasa_items || []).length === 0 && <p className="p-4 text-center text-sm text-gray-400">Tidak ada work item pada unit ini.</p>}
        </div>
      )}
    </div>
  );
}

function KpiTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ technicians: [], stages: [], window: {} });
  const load = useCallback(async () => {
    try { const r = await api.get("/tech/kpi", { params: { period } }); setData(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-2">
      <div className="mb-2 flex flex-wrap gap-1.5">
        {[["today", "Hari"], ["week", "Minggu"], ["month", "Bulan"]].map(([p, l]) => (
          <button key={p} data-testid={`kpi-period-${p}`} onClick={() => setPeriod(p)} className={`rounded-full px-3 py-1 text-xs font-semibold ${period === p ? "bg-[#111827] text-white" : "bg-gray-100 text-gray-600"}`}>{l}</button>
        ))}
        <span className="ml-auto self-center text-[11px] text-gray-400">{data.window?.from} → {data.window?.to}</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[52vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
              <tr><th className="px-3 py-1.5">Teknisi</th><th className="px-3 py-1.5">Assigned</th><th className="px-3 py-1.5">Completed</th><th className="px-3 py-1.5">Approved</th><th className="px-3 py-1.5">Rework</th><th className="px-3 py-1.5">Produktivitas</th><th className="px-3 py-1.5">On-Time</th><th className="px-3 py-1.5">Insentif</th></tr>
            </thead>
            <tbody>
              {data.technicians.map((t) => (
                <tr key={t.technician_user_id} data-testid={`kpi-${t.technician_user_id}`} className="border-t border-gray-100 text-[13px]">
                  <td className="px-3 py-1.5 font-medium text-gray-800">{t.technician_name} <span className="text-[11px] text-gray-400">{t.technician_user_id}</span></td>
                  <td className="px-3 py-1.5">{t.assigned}</td><td className="px-3 py-1.5">{t.completed}</td>
                  <td className="px-3 py-1.5 font-semibold text-emerald-700">{t.approved}</td>
                  <td className="px-3 py-1.5 text-amber-700">{t.rework}</td>
                  <td className="px-3 py-1.5"><span className={`font-bold ${t.productivity >= 85 ? "text-emerald-600" : t.productivity >= 70 ? "text-amber-600" : "text-red-600"}`}>{t.productivity}%</span></td>
                  <td className="px-3 py-1.5">{t.on_time}%</td>
                  <td className="px-3 py-1.5 font-medium text-gray-800">{rupiah(t.eligible_incentive)}</td>
                </tr>
              ))}
              {data.technicians.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-sm text-gray-400">Belum ada data KPI pada periode ini.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {data.stages.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-xs font-semibold text-gray-600">Produktivitas per Stage (approved)</div>
          <div className="flex flex-wrap gap-2">
            {data.stages.map((s) => <span key={s.stage} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">{s.stage.replace(/_/g, " ")}: <b>{s.count}</b></span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function Ledger() {
  const { user } = useAuth();
  const canPay = user?.role === "admin" || [...MGMT, "Finance"].includes(user?.role_name);
  const [data, setData] = useState({ rows: [], summary: {} });
  const load = useCallback(async () => {
    try { const r = await api.get("/tech/incentive-ledger"); setData(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pay = async (id) => {
    try { await api.post(`/tech/incentive/${id}/pay`, {}); toast.success("Insentif dibayar"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const s = data.summary || {};
  return (
    <div className="mt-2">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-gray-100 bg-white p-2.5 text-center shadow-sm"><div className="text-sm font-bold text-emerald-700">{rupiah(s.approved)}</div><div className="text-[10px] uppercase text-gray-400">Approved</div></div>
        <div className="rounded-lg border border-gray-100 bg-white p-2.5 text-center shadow-sm"><div className="text-sm font-bold text-blue-700">{rupiah(s.paid)}</div><div className="text-[10px] uppercase text-gray-400">Paid</div></div>
        <div className="rounded-lg border border-gray-100 bg-white p-2.5 text-center shadow-sm"><div className="text-sm font-bold text-amber-700">{rupiah(s.unpaid)}</div><div className="text-[10px] uppercase text-gray-400">Unpaid</div></div>
      </div>
      <div className="mt-2 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
              <tr><th className="px-3 py-1.5">Tanggal</th><th className="px-3 py-1.5">Teknisi</th><th className="px-3 py-1.5">Nopol</th><th className="px-3 py-1.5">Work Item</th><th className="px-3 py-1.5">Insentif</th><th className="px-3 py-1.5">Status</th><th className="px-3 py-1.5">Aksi</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} data-testid={`ledger-${r.id}`} className="border-t border-gray-100 text-[13px]">
                  <td className="px-3 py-1.5 text-gray-500">{r.completed_date}</td>
                  <td className="px-3 py-1.5 text-gray-700">{r.technician_name || r.technician_user_id}</td>
                  <td className="px-3 py-1.5">{r.license_plate}</td>
                  <td className="px-3 py-1.5 text-gray-600">{r.work_description} <span className="text-[11px] text-gray-400">({r.work_type})</span></td>
                  <td className="px-3 py-1.5 font-medium text-gray-800">{rupiah(r.approved_incentive)}</td>
                  <td className="px-3 py-1.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${LEDGER_ST[r.status] || "bg-gray-100 text-gray-600"}`}>{r.status}</span></td>
                  <td className="px-3 py-1.5">{canPay && ["APPROVED", "HOLD"].includes(r.status) && <Button data-testid={`pay-${r.id}`} size="sm" className="h-7 bg-blue-600 text-xs hover:bg-blue-700" onClick={() => pay(r.id)}>Bayar</Button>}{r.status === "PAID" && <span className="text-[11px] text-blue-600">✓ {r.paid_date || ""}</span>}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-sm text-gray-400">Belum ada insentif.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
